import classNames from "classnames"
import dayjs from "dayjs"
import type {
  ForwardedRef,
  HTMLAttributes,
  MouseEventHandler,
} from "react"
import {
  useCallback,
  forwardRef,
  useState,
} from "react"

import styles from "./Graph.module.scss"
import { useGraphContext } from "./GraphContext"
import { GraphFooter } from "./GraphFooter"
import { GraphHeader } from "./GraphHeader"
import { GraphNormalRange } from "./GraphNormalRange"
import { GraphUrgentLow } from "./GraphUrgentLow"
import { useTooltipContext } from "./TooltipContext"
import { VirtualList } from "./VirtualList"
import { YAxis } from "./YAxis"
import { useFollowedUsers } from "src/contexts"
import { useCreationOverlayPublicContext } from "src/pages/Home/CreationOverlay"

const graphSelectorId = "graph"

interface Props extends HTMLAttributes<HTMLDivElement> {
  updateScrollController: () => void;
}

export const Graph = forwardRef<HTMLDivElement, Props>(
  function Graph(props: Props, propRef: ForwardedRef<HTMLDivElement>) {
    const {
      updateScrollController,
      ...divProps
    } = props

    const {
      currentDeviceSettings,
      currentFollow,
    } = useFollowedUsers()
    const { openCreationOverlay } = useCreationOverlayPublicContext()

    const {
      zoom,
      pointsPerHour,
      graphScrollDate,
    } = useGraphContext()

    // these must be attached to the tooltip trigger
    const {
      tooltipRef,
      getReferenceProps,
    } = useTooltipContext()

    const graphIconScale = currentDeviceSettings.graph_icon_scale

    const [
      isGraphCollapsed,
      setIsGraphCollapsed,
    ] = useState(false)

    /*
     * when the graph container is clicked, start creating an entry for the
     * time at which is was clicked
     */
    const onClick: MouseEventHandler = useCallback(
      (e) => {
        const xPosition = e.clientX
        const distanceFromCenter = xPosition - (zoom * pointsPerHour / 2)

        const time = dayjs(graphScrollDate)
          .add(
            distanceFromCenter / pointsPerHour,
            "hours",
          )
          .toISOString()

        openCreationOverlay(time)
      },
      [
        zoom,
        pointsPerHour,
        graphScrollDate,
        openCreationOverlay,
      ],
    )

    const onKeyPress = useCallback(
      () => {
        // this is a placeholder for accessibility
      },
      [],
    )

    return (
      <div
        {...divProps}
        className={classNames(
          divProps.className,
          styles.wrapper,
        )}
        ref={propRef}
        onFocus={updateScrollController}
        onTouchStart={updateScrollController}
        onMouseOver={updateScrollController}
      >
        <div
          className={classNames(
            styles.collapseContainer,
            isGraphCollapsed && styles.collapsed,
          )}
        >
          <div className={styles.collapsibleContent}>
            <GraphHeader />
            <div className={styles.graphContent}>
              <div
                className={styles.graphContainer}
                ref={tooltipRef}
                data-icon-scale={graphIconScale}
                onClick={currentFollow.is_self ? onClick : undefined}
                role="button"
                tabIndex={-1}
                {...getReferenceProps}
                id={graphSelectorId}
                onKeyPress={onKeyPress}
              >
                <GraphNormalRange />
                <GraphUrgentLow />
                <VirtualList />
              </div>
              <YAxis />
            </div>
          </div>
        </div>
        <GraphFooter
          isGraphCollapsed={isGraphCollapsed}
          setIsGraphCollapsed={setIsGraphCollapsed}
        />
      </div>
    )
  },
)
